import { useId } from 'react'
import SkillPill from './SkillPill'
import Icon from './Icon'
import styles from './SkillCard.module.css'

/**
 * SkillCard — displays a skill with coverage metrics, a progress bar, and a recommendation.
 *
 * Props:
 *   skillName      {string}    — name of the skill (e.g. "Kubernetes")
 *   skillClass     {string}    — skill class label (e.g. "Infrastructure")
 *   skillVariant   {string}    — SkillPill variant (e.g. "infrastructure")
 *   description    {string}    — optional evidence-based skill description; when present,
 *                                an info icon next to the type pill shows it on hover/focus.
 *                                Hidden entirely when there's no description.
 *   current        {number}    — current number of people with this skill (e.g. 4)
 *   target         {number}    — target number of people (e.g. 10)
 *   percent        {number}    — progress bar fill percentage (e.g. 60)
 *   status         {string}    — "Insufficient" | "Adequate" | "Strong"
 *   recommendation {string}    — recommendation text below the bar
 *   color          {string}    — optional card background color (e.g. "#1a3a4a" or "var(--color-blue-800)")
 *   onSkillClick   {function}  — called when the skill name is clicked
 *   onPeopleClick  {function}  — called when the people count is clicked
 */
export default function SkillCard({
  skillName,
  skillClass,
  skillVariant,
  description,
  current,
  target,
  percent,
  status,
  recommendation,
  color,
  onSkillClick,
  onPeopleClick,
}) {
  const statusVariant = getStatusVariant(status)
  const bgColor = color
    ? color.startsWith('--') ? `var(${color})` : color
    : undefined
  const tooltipId = useId()

  return (
    // The whole card is the actionable surface — clicking anywhere opens the
    // Skill Details modal. Inner controls that have their own behavior (the people
    // count, when an onPeopleClick is supplied) stop propagation so they don't also
    // open it; otherwise clicks bubble up here.
    <div
      className={`${styles.card} ${onSkillClick ? styles.clickable : ''}`}
      style={bgColor ? { backgroundColor: bgColor } : undefined}
      onClick={onSkillClick}
      onKeyDown={onSkillClick ? e => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onSkillClick(e)
        }
      } : undefined}
      role={onSkillClick ? 'button' : undefined}
      tabIndex={onSkillClick ? 0 : undefined}
    >

      {/* Top row — skill name, pill, people count, status */}
      <div className={styles.topRow}>
        <div className={styles.leftGroup}>
          <span className={styles.skillName}>{skillName}</span>
          <SkillPill label={skillClass} variant={skillVariant} />
          {description && (
            <div className={styles.infoWrap}>
              <button
                type="button"
                className={styles.infoBtn}
                aria-label={`Description for ${skillName}`}
                aria-describedby={tooltipId}
                onClick={e => e.stopPropagation()}
              >
                <Icon name="info" />
              </button>
              <div id={tooltipId} className={styles.tooltip} role="tooltip">
                {description}
              </div>
            </div>
          )}
        </div>
        <div className={styles.rightGroup}>
          <button
            className={styles.peopleCount}
            onClick={onPeopleClick ? e => { e.stopPropagation(); onPeopleClick(e) } : undefined}
          >
            <Icon name="people" />
            {current}/{target}
          </button>
          <span className={`${styles.status} ${styles[statusVariant]}`}>
            {getStatusIcon(status)}
            {status}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className={styles.progressTrack}>
        <div
          className={`${styles.progressFill} ${styles[statusVariant]}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>

      {/* Bottom row — recommendation + percent */}
      <div className={styles.bottomRow}>
        <span className={styles.recommendation}>{recommendation}</span>
        <span className={styles.percent}>{percent}%</span>
      </div>

    </div>
  )
}

function getStatusVariant(status) {
  if (!status) return 'neutral'
  const s = status.toLowerCase()
  if (s === 'insufficient') return 'insufficient'
  if (s === 'adequate')     return 'adequate'
  if (s === 'strong')       return 'strong'
  return 'neutral'
}

function getStatusIcon(status) {
  if (!status) return null
  const s = status.toLowerCase()

  if (s === 'insufficient') return <Icon name="info" />
  if (s === 'adequate') return <Icon name="status-neutral" />
  if (s === 'strong') return <Icon name="thumb" />
  return null
}